import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiStar, FiScissors, FiHeart, FiAward } = FiIcons;

const Home = () => {
  const services = [
    {
      icon: FiScissors,
      title: 'Hair Styling',
      description: 'Professional cuts, colors, and styling'
    },
    {
      icon: FiHeart,
      title: 'Facial Treatments',
      description: 'Rejuvenating facial and skin care'
    },
    {
      icon: FiAward,
      title: 'Nail Art',
      description: 'Beautiful manicures and pedicures'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      text: 'Amazing service! The stylists are incredibly talented.',
      rating: 5
    },
    {
      name: 'Emily Davis',
      text: 'Love my new look! Highly recommend this salon.',
      rating: 5
    },
    {
      name: 'Jessica Wilson',
      text: 'Professional and friendly staff. Great experience!',
      rating: 5
    }
  ];

  return (
    <>
      <Helmet>
        <title>Luxe Salon - Premium Beauty & Hair Styling Services | Expert Stylists</title>
        <meta name="description" content="Experience luxury beauty services at Luxe Salon. Professional hair styling, facial treatments, nail art, and makeup services. Book your appointment today with our expert stylists." />
        <meta name="keywords" content="luxury salon, hair styling, beauty services, facial treatments, nail art, makeup, professional stylists, beauty salon" />
        <meta property="og:title" content="Luxe Salon - Premium Beauty & Hair Styling Services" />
        <meta property="og:description" content="Transform your look with our expert stylists and premium beauty services. Hair styling, facials, nails, and more." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Luxe Salon - Premium Beauty Services" />
        <meta name="twitter:description" content="Experience luxury beauty at Luxe Salon. Professional styling and premium services." />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-black via-red-900/20 to-black overflow-hidden">
          <div className="absolute inset-0 bg-black/40"></div>
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80)'
            }}
          ></div>
          
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
            <motion.h1 
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-5xl md:text-7xl font-bold mb-6"
            >
              <span className="bg-gradient-to-r from-red-400 via-yellow-400 to-red-400 bg-clip-text text-transparent">
                Luxury Beauty
              </span>
              <br />
              <span className="text-white">Experience</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="text-xl md:text-2xl mb-8 text-gray-200"
            >
              Transform your look with our expert stylists and premium services
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link 
                to="/services"
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
              >
                View Services
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
              <Link 
                to="/contact"
                className="border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
              >
                Book Appointment
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Services Preview */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-red-400 via-yellow-400 to-red-400 bg-clip-text text-transparent">
                  Our Services
                </span>
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Discover our range of premium beauty services designed to enhance your natural beauty
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-gradient-to-br from-red-900/20 to-black border border-red-500/20 rounded-lg p-8 text-center hover:border-yellow-400/40 transition-all duration-300"
                >
                  <div className="bg-gradient-to-r from-red-500 to-yellow-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-300">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-red-400 via-yellow-400 to-red-400 bg-clip-text text-transparent">
                  What Our Clients Say
                </span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className="bg-gradient-to-br from-red-900/20 to-black border border-red-500/20 rounded-lg p-6 text-center"
                >
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                  <h4 className="text-white font-semibold">{testimonial.name}</h4>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;